/**
 * Shared address constants.
 *
 * State/province abbreviation lists for the countries we support (US and CA),
 * along with the derived types and a small lookup helper. Keep this file
 * data-only — anything that does real work on an address (validation,
 * formatting, normalization) belongs in `assets/utils/address.ts` and may
 * import from here.
 */

/** Country codes we currently support for address entry. */
export type CountryCode = 'US' | 'CA';

/** US state and territory abbreviations (USPS codes), alphabetical. */
export const US_STATE_CODES = [
  'AL',
  'AK',
  'AZ',
  'AR',
  'CA',
  'CO',
  'CT',
  'DE',
  'DC',
  'FL',
  'GA',
  'HI',
  'ID',
  'IL',
  'IN',
  'IA',
  'KS',
  'KY',
  'LA',
  'ME',
  'MD',
  'MA',
  'MI',
  'MN',
  'MS',
  'MO',
  'MT',
  'NE',
  'NV',
  'NH',
  'NJ',
  'NM',
  'NY',
  'NC',
  'ND',
  'OH',
  'OK',
  'OR',
  'PA',
  'RI',
  'SC',
  'SD',
  'TN',
  'TX',
  'UT',
  'VT',
  'VA',
  'WA',
  'WV',
  'WI',
  'WY',
] as const;

/** Canadian province and territory abbreviations, alphabetical. */
export const CA_PROVINCE_CODES = [
  'AB',
  'BC',
  'MB',
  'NB',
  'NL',
  'NS',
  'NT',
  'NU',
  'ON',
  'PE',
  'QC',
  'SK',
  'YT',
] as const;

/** A valid US state/territory code. */
export type UsStateCode = (typeof US_STATE_CODES)[number];

/** A valid Canadian province/territory code. */
export type CaProvinceCode = (typeof CA_PROVINCE_CODES)[number];

/** A valid state/province code in any country we support. */
export type StateOrProvinceCode = UsStateCode | CaProvinceCode;

/**
 * Returns the state/province codes for a given country, or an empty array
 * if the country is unknown/unspecified. Callers can pass `string | undefined`
 * directly (e.g. raw form state) without narrowing first.
 */
export const getStateCodesForCountry = (
  country: CountryCode | string | undefined,
): readonly string[] => {
  if (country === 'US') return US_STATE_CODES;
  if (country === 'CA') return CA_PROVINCE_CODES;
  return [];
};
